import {CloseIcon} from "assets/icons";
import DownloadIcon from "assets/icons/Download";
import UploadIcon from "assets/icons/Upload";
import classNames from "classnames";
import {Button, Loader, ResetFilters, ToastContent} from "components";
import {fixMojibake, linkedInUrlPurify} from "helpers/string";
import {useAppDispatch, useAppSelector} from "hooks";
import {TProfilesImportInProgress, TUrlImportItem} from "mapx-components/BulkImport/types";
import Papa from "papaparse";
import React, {ChangeEvent, useEffect, useMemo, useRef, useState} from "react";
import {BiRefresh} from "react-icons/bi";
import {toast} from "react-toastify";
import {setImportLinkedInProfileInfoForCurrentImport} from "store/background-tasks/backgroundTaskActions";
import {removeBulkImportDataForSource} from "store/bulk-import/bulkImportActions";
import {projectSelector} from "store/project-list/projectListSelectors";
import {formatETA, isInProgressStatus} from "../Report/utils";
import styles from "../bulkImportModal.module.scss";
import type {TFormProps} from "./types";

const Form: React.FC<TFormProps> = ({
	handleSubmit,
	inProgress,
	isSubmitting,
	otherImportInstancePresent,
	importedLinkedInUrls,
	linkedInUrl,
	handleDownload,
	source,
	marketMapId,
	loadingImportHistoryDownload,
	labels,
	validateUrl,
}) => {
	const dispatch = useAppDispatch();

	const [fileName, setFileName] = useState<string>("");

	const [userTyping, setUserTyping] = useState(false);

	const [input, setInput] = useState(""); // Stores the raw input from the textarea

	const [inputArray, setInputArray] = useState<string[]>([]); // Stores parsed LinkedIn URLs extracted from user input or CSV file

	const project = useAppSelector(projectSelector);

	const hasErrorRef = useRef<string[]>([]);

	const textareaRef = useRef<HTMLTextAreaElement>(null);

	/**
	 * Handles changes in the textarea input &
	 * Extracts valid LinkedIn URLs from the pasted text.
	 */
	const handleOnChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const newValue = e.target.value.replace(/\n/g, " ");

		const result = linkedInUrlPurify(newValue);

		const validURLs = getValidURLs(result);

		validURLs.map((url) => {
			if (!inputArray.includes(url)) {
				setInput((prevInput) => prevInput + (prevInput ? "\n" : "") + url);
				setInputArray((prev) => [...prev, url]);
			}
		});
	};

	/**
	 * Extracts and validates LinkedIn URLs from user input or a CSV file.
	 * Filters out invalid and duplicate URLs.
	 */
	const getValidURLs = (inputURLs: string) => {
		const decodedInput = fixMojibake(inputURLs);
		setUserTyping(false);

		const separatedValues = decodedInput?.split(/(https?:\/\/)/);

		let result = separatedValues.shift() || "";

		for (const element of separatedValues) {
			if (element.startsWith("https://") || element.startsWith("http://")) {
				result += "\n";
			}
			result += element;
		}

		// Add newline character at the end
		result += "\n";

		const urlsArray = result
			.split("\n")
			.map((url) => url.trim())
			.filter((url: string) => url !== "");

		const uniqueUrls = Array.from(new Set(urlsArray));

		hasErrorRef.current = uniqueUrls.filter((url: string) => !validateUrl(url));

		const duplicates = uniqueUrls.filter((url: string) => inputArray.includes(url));

		const toastTitle =
			hasErrorRef.current.length > 0
				? "Invalid URL filtered out"
				: duplicates.length > 0
				? "Duplicate URL filtered out"
				: duplicates.length > 0 &&
				  hasErrorRef.current.length > 0 &&
				  "Invalid, & duplicate URLs filtered out";

		(hasErrorRef.current.length > 0 || duplicates.length > 0) &&
			toast.warning(ToastContent, {
				autoClose: 3000,
				closeOnClick: true,
				data: {
					title: toastTitle,
				},
				delay: 0,
			});

		textareaRef?.current?.focus();

		return uniqueUrls.filter((url: string) => validateUrl(url));
	};

	const submit = () => {
		handleSubmit(inputArray);
	};

	const handleFileUpload = (e: ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files && e.target.files[0];

		if (file) {
			setFileName(file.name);
		}

		if (
			file &&
			file.type !== "application/vnd.ms-excel" &&
			file.name.split(".").pop() !== "csv"
		) {
			toast.warning(ToastContent, {
				autoClose: 3000,
				closeOnClick: true,
				data: {
					title: "Please upload a CSV file",
				},
				delay: 0,
			});

			return; // Exit early if the file is not a CSV file
		}

		const reader = new FileReader();

		reader.onload = () => {
			const result = reader.result;
			// eslint-disable-next-line
			// @ts-ignore
			const data = Papa.parse(result, {header: true}).data;

			// Check if the 'linkedin_url' column exists in the uploaded file
			const linkedinUrlExists = data.some((row: {linkedin_url: string}) =>
				Object.keys(row).includes("linkedin_url"),
			);

			if (!linkedinUrlExists) {
				toast.warning(ToastContent, {
					autoClose: 3000,
					closeOnClick: true,
					data: {
						title: "File contains no entry named linkedin_url",
					},
					delay: 0,
				});

				return; // Exit early if 'linkedin_url' column does not exist
			}
			if (data.length < 2) {
				toast.warning(ToastContent, {
					autoClose: 3000,
					closeOnClick: true,
					data: {
						title: "File contain no URL's",
					},
					delay: 0,
				});

				return; // Exit early if 'linkedin_url' column does not have any entry
			}

			const extractedUrls = data
				.filter((row: {linkedin_url: string}) => row.linkedin_url?.trim() !== "")
				.map((row: {linkedin_url: string}) => {
					const trimmedUrl = row.linkedin_url?.trim();
					const parts = trimmedUrl.split("?")[0].split("/");

					return parts.slice(0, 5).join("/");
				});

			const formattedUrls = extractedUrls.join("\n");

			const validURLs = getValidURLs(formattedUrls);

			validURLs.map((url) => {
				if (!inputArray.includes(url)) {
					setInput((prevInput) => prevInput + (prevInput ? "\n" : "") + url);
					setInputArray((prev) => [...prev, url]);
				}
			});
		};

		if (file) {
			reader.readAsText(file);
		}
	};

	const setImportStartPayload = useMemo(
		() => ({
			progress: 0,
			purpose: "import",
			status: "waiting",
			linkedin_candidate_urls: [] as TUrlImportItem[],
			linkedin_company_urls: [] as TUrlImportItem[],
			import_type:
				labels.entityName === "Company"
					? "Companies Bulk Import"
					: "Candidates Bulk Import",
			project_id: source === "project" ? project?.id : null,
			market_map_id: source === "market_map" ? marketMapId : null,
		}),
		[source, project, marketMapId, labels.entityName],
	);

	useEffect(() => {
		// saving the input to global state
		if (inputArray.length > 0) {
			const mappedUrls = inputArray.map((url) => ({
				status: "waiting",
				url,
			}));

			if (labels.entityName === "Company") {
				setImportStartPayload.linkedin_company_urls = mappedUrls;
				setImportStartPayload.linkedin_candidate_urls = [];
			} else {
				setImportStartPayload.linkedin_candidate_urls = mappedUrls;
				setImportStartPayload.linkedin_company_urls = [];
			}

			dispatch(
				setImportLinkedInProfileInfoForCurrentImport(
					setImportStartPayload as TProfilesImportInProgress,
				),
			);
		}
	}, [inputArray, dispatch, setImportStartPayload, labels.entityName]);

	const handleRemoveUrl = (index: number, url: string) => {
		setUserTyping(false);

		const newArray = [...inputArray];

		newArray.splice(index, 1);

		setInputArray(newArray);

		const regex = new RegExp(`(${url.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "g");

		const filteredInput = input.replace(regex, "");

		setInput(filteredInput.trim());

		const mappedUrls = newArray.map((url) => ({
			status: "waiting",
			url,
		}));

		if (labels.entityName === "Company") {
			setImportStartPayload.linkedin_company_urls = mappedUrls;
			setImportStartPayload.linkedin_candidate_urls = [];
		} else {
			setImportStartPayload.linkedin_candidate_urls = mappedUrls;
			setImportStartPayload.linkedin_company_urls = [];
		}

		dispatch(
			setImportLinkedInProfileInfoForCurrentImport(
				setImportStartPayload as TProfilesImportInProgress,
			),
		);

		if (inputArray.length < 7) {
			textareaRef?.current?.focus();
		}
	};

	useEffect(() => {
		// if stored linkedin value exists in global state, assign it to the form input
		if (linkedInUrl) {
			const urls = linkedInUrl.map((item) => item.url);
			if (inputArray.length === 0) {
				setInputArray(urls);
			}
		}
	}, [linkedInUrl, inputArray.length]);

	const downloadCSV = () => {
		const csvUrl = window.location.origin + "/files/linkedIn_import_template.csv";

		const link = document.createElement("a");
		link.href = csvUrl;
		link.setAttribute("download", "linkedIn_import_template.csv");
		document.body.appendChild(link);

		link.click();

		document.body.removeChild(link);
	};

	const handleResetClick = () => {
		setInputArray([]);

		setInput("");

		setFileName("");

		const sourceId = source === "market_map" ? marketMapId ?? 0 : project?.id ?? 0;

		dispatch(removeBulkImportDataForSource(sourceId));

		textareaRef?.current?.focus();
	};

	const isItActionClick = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
		return event.altKey || event.ctrlKey || (event.metaKey && event.key === "v");
	};

	const handleKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = (event) => {
		if (!isItActionClick(event)) {
			setUserTyping(true);

			event.preventDefault();
		}
	};

	/**
	 * Determines the dynamic height of the textarea based on the number of URLs entered.
	 */
	const calculateTextareaHeight = () => {
		switch (inputArray.length) {
			case 0:
				return "220px";
			case 1:
				return "160px";
			case 2:
				return "110px";
			case 3:
				return "90px";
			case 4:
				return "26px";
			default:
				return "30px";
		}
	};

	/**
	 * Gets the ETA for a specific URL from the linkedInUrl array.
	 * Returns formatted ETA string or null if not available or profile is not in-progress.
	 */
	const getEtaForUrl = (url: string): string | null => {
		if (!linkedInUrl) return null;
		const urlInfo = linkedInUrl.find((item) => item.url === url);
		if (!urlInfo || !isInProgressStatus(urlInfo.status)) return null;
		const eta = formatETA(urlInfo.eta_expected_at, urlInfo.eta_original);

		return eta !== "-" ? eta : null;
	};

	return (
		<div className={styles.wrapper__form}>
			<div className={styles.wrapper__title}>{labels.modalTitle}</div>

			<div className={styles.wrapper__subtitle}>
				<p>LinkedIn URLs</p>
				<div className={styles.resetArea} data-testid="clearAll">
					{inputArray?.length > 0 && (
						<ResetFilters
							parentStyle={{color: "#5A5A5A", marginRight: 19}}
							onClick={handleResetClick}
							displayIcon={true}
						>
							Clear All
						</ResetFilters>
					)}
				</div>
			</div>
			{
				<div className={styles.form_container} data-testid="removeUrlDiv">
					{inputArray?.map((url, index) => {
						const eta = getEtaForUrl(url);

						return (
							<div key={index} className={styles.linkedinTag}>
								<p>
									{url}
									{eta && <span className={styles.etaInfo}>ETA: {eta}</span>}
									<span>
										<CloseIcon
											title={"Remove this URL"}
											color={"#837D7C"}
											onClick={() => handleRemoveUrl(index, url)}
										/>
									</span>
								</p>
							</div>
						);
					})}
					<textarea
						style={{height: calculateTextareaHeight()}}
						ref={textareaRef}
						value=""
						rows={6}
						className={styles.textAreaForPaste}
						placeholder={labels.urlPlaceholder}
						onChange={handleOnChange}
						onKeyDown={handleKeyDown}
						disabled={inProgress}
					/>
				</div>
			}

			<small style={{color: "#b33636"}} hidden={!userTyping}>
				You can only paste linkedin URLs here
			</small>
			<p style={{textAlign: "center", color: "#888888"}}> or</p>
			<p style={{fontSize: "15px", color: "#888888"}}>Upload CSV file</p>

			<div className={styles.custom_file_upload}>
				<input
					type="file"
					id="file-upload"
					className={styles.input_file}
					onChange={handleFileUpload}
				/>
				<label htmlFor="file-upload" className={styles.file_label}>
					<div className={styles.left_half}>
						<span className={styles.placeholder}>
							{fileName
								? fileName.slice(0, 40) + (fileName.length > 35 ? "..." : "")
								: "Upload your document..."}
						</span>
					</div>
					<div className={styles.right_half}>
						<UploadIcon /> Upload File
					</div>
				</label>
			</div>

			<div className={styles.downloadTemplate}>
				<a onClick={downloadCSV}>
					<DownloadIcon /> <span>Download the CSV template</span>
				</a>

				{importedLinkedInUrls && importedLinkedInUrls.length > 0 && (
					<a
						onClick={() => handleDownload(null)}
						data-testid="DownloadBulkImportCsvButton"
						className={classNames(styles.downloadAllReport, {
							[styles.disabled]: loadingImportHistoryDownload,
						})}
					>
						{loadingImportHistoryDownload ? (
							<Loader type={"TailSpin"} height={"13px"} width={"12px"} />
						) : (
							<DownloadIcon />
						)}
						<span>Download previous imports</span>
					</a>
				)}
			</div>

			<Button
				data-testid="SubmitImport"
				name={labels.triggerButtonLabel}
				type={"button"}
				className={classNames(styles.button, styles.wrapper__form__btn)}
				onClick={submit}
				disabled={
					inputArray.length === 0 ||
					inProgress ||
					isSubmitting ||
					otherImportInstancePresent
				}
			>
				{(inProgress || isSubmitting) && (
					<BiRefresh size={22} className={classNames(styles.refresh)} />
				)}{" "}
				{inProgress
					? `${labels.importingLabel}...`
					: isSubmitting
					? "Submitting..."
					: otherImportInstancePresent
					? "Another Import Is In Progress!"
					: `Start Importing`}
			</Button>
		</div>
	);
};

export default Form;
